import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import DialogActions from "@mui/material/DialogActions";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";

/** Shared visual tokens for supplier internal + external pages */
export const supplierUi = {
	pageBg: grey[50],
	surface: "#ffffff",
	border: grey[200],
	borderStrong: grey[300],
	textPrimary: grey[900],
	textSecondary: grey[600],
	textMuted: grey[500],
	radius: 2,
	shadow: "0 1px 2px rgba(16, 24, 40, 0.04), 0 1px 3px rgba(16, 24, 40, 0.06)",
	status: {
		Approved: { bg: "#ECFDF3", color: "#027A48", border: "#A6F4C5" },
		Rejected: { bg: "#FEF3F2", color: "#B42318", border: "#FECDCA" },
		Pending: { bg: "#FFFAEB", color: "#B54708", border: "#FEDF89" },
		Active: { bg: "#ECFDF3", color: "#027A48", border: "#A6F4C5" },
		inactive: { bg: "#F2F4F7", color: "#475467", border: "#D0D5DD" },
	},
};

export const SupplierStatusChip = ({ label, size = "small" }) => {
	const tone = supplierUi.status[label] || supplierUi.status.Pending;
	return (
		<Chip
			label={label}
			size={size}
			sx={{
				height: 26,
				fontWeight: 600,
				fontSize: 13,
				letterSpacing: 0.2,
				bgcolor: tone.bg,
				color: tone.color,
				border: `1px solid ${tone.border}`,
				borderRadius: 1.5,
				"& .MuiChip-label": { px: 1.25 },
			}}
		/>
	);
};

export const SupplierPageShell = ({ children, sx = {} }) => (
	<Box
		sx={{
			width: "100%",
			minHeight: "100%",
			bgcolor: supplierUi.pageBg,
			px: { xs: 1.5, md: 2.5 },
			py: { xs: 2, md: 2.5 },
			boxSizing: "border-box",
			...sx,
		}}>
		<Box sx={{ width: "100%", maxWidth: 1280, mx: "auto" }}>{children}</Box>
	</Box>
);

export const SupplierPageHeader = ({ title, subtitle, actions, sx = {} }) => (
	<Stack
		direction={{ xs: "column", sm: "row" }}
		alignItems={{ xs: "flex-start", sm: "center" }}
		justifyContent="space-between"
		spacing={1.5}
		sx={{ mb: 2.5, ...sx }}>
		<Box minWidth={0}>
			<Typography
				variant="h5"
				sx={{
					fontWeight: 600,
					fontSize: { xs: 24, md: 28 },
					letterSpacing: "-0.02em",
					color: supplierUi.textPrimary,
					lineHeight: 1.25,
				}}>
				{title}
			</Typography>
			{subtitle ? (
				typeof subtitle === "string" ? (
					<Typography variant="body2" sx={{ color: supplierUi.textSecondary, mt: 0.5, fontSize: 15 }}>
						{subtitle}
					</Typography>
				) : (
					<Box mt={0.75}>{subtitle}</Box>
				)
			) : null}
		</Box>
		{actions ? (
			<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
				{actions}
			</Stack>
		) : null}
	</Stack>
);

export const SupplierSection = ({ title, actions, children, sx = {} }) => (
	<Paper
		elevation={0}
		sx={{
			width: "100%",
			bgcolor: supplierUi.surface,
			border: `1px solid ${supplierUi.border}`,
			borderRadius: supplierUi.radius,
			boxShadow: supplierUi.shadow,
			overflow: "hidden",
			...sx,
		}}>
		<Stack
			direction={{ xs: "column", sm: "row" }}
			alignItems={{ xs: "stretch", sm: "center" }}
			justifyContent="space-between"
			spacing={1.5}
			sx={{
				px: { xs: 2, md: 2.5 },
				py: 1.75,
				borderBottom: `1px solid ${supplierUi.border}`,
				bgcolor: grey[50],
			}}>
			<Typography
				variant="subtitle1"
				sx={{
					fontWeight: 600,
					fontSize: 17,
					letterSpacing: 0.1,
					color: supplierUi.textPrimary,
				}}>
				{title}
			</Typography>
			{actions ? (
				<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
					{actions}
				</Stack>
			) : null}
		</Stack>
		<Box sx={{ px: { xs: 2, md: 2.5 }, py: { xs: 2, md: 2.5 } }}>{children}</Box>
	</Paper>
);

/** Edit-modal layout tokens (supplier pages only) */
export const modalFormStack = {
	width: { xs: "100%", sm: "47%", md: "31%" },
};

export const modalFormStackLg = {
	width: { xs: "100%", sm: "100%", md: "47%" },
};

export const modalLabelSx = {
	fontSize: 14,
	fontWeight: 500,
	color: grey[600],
	mb: 0.75,
	letterSpacing: 0.15,
};

export const supplierModalPaperProps = {
	sx: {
		borderRadius: 2,
		border: `1px solid ${grey[200]}`,
		boxShadow: "0 8px 24px rgba(16, 24, 40, 0.12)",
		overflow: "hidden",
	},
};

export const modalContentSx = {
	width: "100%",
	px: { xs: 2, md: 3 },
	pt: 0,
	pb: 1,
	boxSizing: "border-box",
	"& .MuiInputLabel-root": modalLabelSx,
	"& .MuiOutlinedInput-root": {
		borderRadius: 1.5,
		bgcolor: "#fff",
		fontSize: 15,
	},
	"& .MuiInputBase-input": {
		fontSize: 15,
	},
	"& .MuiOutlinedInput-notchedOutline": {
		borderColor: grey[300],
	},
};

export const modalCancelBtnSx = {
	textTransform: "none",
	fontWeight: 600,
	borderRadius: 1.5,
	borderColor: grey[300],
	color: grey[900],
	px: 2,
};

export const modalSaveBtnSx = {
	textTransform: "none",
	fontWeight: 600,
	borderRadius: 1.5,
	boxShadow: "none",
	px: 2.25,
	"&:hover": { boxShadow: "none" },
};

export const SupplierModalHeader = ({ title, subtitle }) => (
	<Box
		sx={{
			px: { xs: 2, md: 3 },
			pt: 2.5,
			pb: 2,
			borderBottom: `1px solid ${grey[200]}`,
			bgcolor: grey[50],
		}}>
		<Typography
			sx={{
				fontWeight: 600,
				fontSize: 20,
				letterSpacing: "-0.01em",
				color: grey[900],
			}}>
			{title}
		</Typography>
		{subtitle ? (
			<Typography variant="body2" sx={{ color: grey[600], mt: 0.5, fontSize: 14 }}>
				{subtitle}
			</Typography>
		) : null}
	</Box>
);

export const SupplierModalActions = ({
	onCancel,
	submitLabel = "Save",
	cancelLabel = "Cancel",
	disableSubmit = false,
	hideCancel = false,
}) => (
	<DialogActions
		sx={{
			px: { xs: 2, md: 3 },
			py: 2,
			borderTop: `1px solid ${grey[200]}`,
			bgcolor: "#fff",
			gap: 1,
		}}>
		{!hideCancel ? (
			<Button variant="outlined" onClick={onCancel} sx={modalCancelBtnSx}>
				{cancelLabel}
			</Button>
		) : null}
		<Button autoFocus variant="contained" type="submit" disabled={disableSubmit} sx={modalSaveBtnSx}>
			{submitLabel}
		</Button>
	</DialogActions>
);
